import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
} from '@tanstack/react-router';
import React from 'react';
import { Sidebar } from './components/Sidebar';
import { AuthGuard } from './lib/auth';
import { SidebarProvider } from './lib/sidebar';
import { LoginPage } from './pages/Login';
import { DashboardPage } from './pages/Dashboard';
import { TasksPage } from './pages/Tasks';
import { TaskDetailPage } from './pages/TaskDetail';
import { NewTaskPage } from './pages/NewTask';
import { CertsPage } from './pages/Certs';
import { DomainsPage } from './pages/Domains';
import { ProjectsPage } from './pages/Projects';
import { ProjectDetailPage } from './pages/ProjectDetail';
import { DocsPage } from './pages/Docs';
import { SettingsPage } from './pages/Settings';
import { AgentsPage } from './pages/Agents';
import { ApisPage } from './pages/Apis';

function AppLayout() {
  return (
    <AuthGuard>
      <SidebarProvider>
        <div className="h-screen overflow-hidden">
          <Sidebar />
          <main
            className="h-full overflow-y-auto ml-0 md:ml-60"
            style={{ background: 'var(--tg-bg)' }}
          >
            <Outlet />
          </main>
        </div>
      </SidebarProvider>
    </AuthGuard>
  );
}

const rootRoute = createRootRoute();

const layoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'layout',
  component: AppLayout,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
});

const dashboardRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/',
  component: DashboardPage,
});

const tasksRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/tasks',
  component: TasksPage,
});

const newTaskRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/tasks/new',
  component: NewTaskPage,
});

const taskDetailRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/tasks/$taskId',
  component: TaskDetailPage,
});

const certsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/certs',
  component: CertsPage,
});

const domainsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/domains',
  component: DomainsPage,
});

const projectsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/projects',
  component: ProjectsPage,
});

const projectDetailRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/projects/$slug',
  component: ProjectDetailPage,
});

const docsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/docs',
  component: DocsPage,
});

const settingsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/settings',
  component: SettingsPage,
});

const agentsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/agents',
  component: AgentsPage,
});

const apisRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/apis',
  component: ApisPage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  layoutRoute.addChildren([
    dashboardRoute,
    tasksRoute,
    newTaskRoute,
    taskDetailRoute,
    certsRoute,
    domainsRoute,
    projectsRoute,
    projectDetailRoute,
    agentsRoute,
    apisRoute,
    docsRoute,
    settingsRoute,
  ]),
]);

function RouterError({ error }: { error: Error }) {
  React.useEffect(() => {
    import('./main').then((m) =>
      m.reportClientError(`router: ${error?.message || error}`, error?.stack)
    ).catch(() => {});
  }, [error]);
  return (
    <div style={{ padding: 24, fontFamily: 'system-ui', color: '#fff', background: '#17212b', minHeight: '100vh' }}>
      <h2 style={{ color: '#e74c3c' }}>UI помилка (роутер)</h2>
      <pre style={{ whiteSpace: 'pre-wrap', fontSize: 12, background: '#232e3c', padding: 12, borderRadius: 8 }}>
        {String(error?.stack || error?.message || error)}
      </pre>
      <button onClick={() => location.reload()} style={{ padding: '8px 16px', background: '#6ab3f3', color: '#000', border: 0, borderRadius: 6, marginTop: 12 }}>
        Перезавантажити
      </button>
    </div>
  );
}

export const router = createRouter({ routeTree, defaultErrorComponent: RouterError });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
